import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";

import { MasterDataSearchKeyWordEnum } from "@/libs/enum/master-search-key.enum";
import { MasterServices } from "@/libs/services/master/master.service";
import { BrandSearchData, SmartSearchMasterData } from "@/libs/types/smart-search.type";
import { useQuery } from "@tanstack/react-query";

type TSmartSearchContext = {
  isFocus: boolean;
  setIsFocus: (isFocus: boolean) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  isLoading: boolean;
  searchResult: SmartSearchMasterData[];
};

const SmartSearchContext = createContext<TSmartSearchContext | undefined>(undefined);

export const SmartSearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchText, setSearchText] = useState<string>("");
  const [isFocus, setIsFocus] = useState<boolean>(false);

  const keyword = MasterDataSearchKeyWordEnum.BRANDSEARCH;

  const fetchMasterData = async () => {
    const response = await MasterServices.getMasterBrandSearchData({ SearchKeyword: keyword });
    return response.SearchFilter;
  };

  const { data, isLoading } = useQuery<BrandSearchData[]>({
    queryKey: [keyword],
    queryFn: fetchMasterData,
    enabled: isFocus,
  });

  const masterBrandSearch = useMemo(() => {
    if (!data) return [];

    return data.flatMap((brand) => {
      const result: SmartSearchMasterData[] = [];
      result.push({
        brand: { id: brand.Id, name: brand.Name },
        model: null,
        fullTextSearch: brand.Name,
      });

      brand.Model.forEach((model) => {
        result.push({
          brand: { id: brand.Id, name: brand.Name },
          model: { id: model.Id, name: model.Name },
          fullTextSearch: `${brand.Name} ${model.Name}`,
        });
      });

      return result;
    });
  }, [data]);

  const searchResult = useMemo(() => {
    if (!searchText) return [];
    return masterBrandSearch.filter((item) =>
      item.fullTextSearch.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [masterBrandSearch, searchText]);

  return (
    <SmartSearchContext.Provider
      value={{ isFocus, isLoading, searchResult, searchText, setSearchText, setIsFocus }}
    >
      {children}
    </SmartSearchContext.Provider>
  );
};

export const useSmartSearch = () => {
  const context = useContext(SmartSearchContext);
  if (!context) {
    throw new Error("useSmartSearch must be used within a SmartSearchProvider");
  }
  return context;
};
